import _extends from "@babel/runtime/helpers/extends";
import React, { useEffect } from 'react';
import { track, events } from 'src/configs/amplitude';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Icon, InputWithIcon } from '@livable/saas-react-components';
import { Typography, TextField, Button, Grid } from '@mui/material';
import SplitPageContainer from 'src/components/shared/SplitPageContainer';
import { PhoneNumberField } from 'src/components/shared/PhoneNumberField';
import { isValidE164 } from 'src/utils/phone';
import inviteSvg from 'src/img/invite.svg';
import Countdown from 'react-countdown';
import { mfaPhoneSchema, mfaSmsCodeInitialValues, mfaSmsCodeSchema } from 'src/utils/formValidation/MFA';
import { observer } from 'mobx-react-lite';
import { useStore } from 'src/stores';
import { useFormik } from 'formik';
import useStyles from './styles';
import InputMask from 'react-input-mask';
import { parseJwt } from 'src/core/services/jwt';
import { AUTH_TOKEN_KEYS } from 'src/constants/authToken';
export const TwoFactorAuthPage = observer(() => {
  const store = useStore();
  const classes = useStyles();
  const navigate = useNavigate();
  const _useTranslation = useTranslation(),
    t = _useTranslation.t;
  const page = 'MFA';
  const userData = parseJwt(localStorage.getItem(AUTH_TOKEN_KEYS.accessToken));
  const toggleAuthEnabling = () => {
    store.account.mfaFlowStore.toggleMFA();
    if (store.account.mfaFlowStore.isEnabled) {
      track(events.clicked('Enable MFA'), {
        page
      });
    } else {
      track(events.clicked('Disable MFA'), {
        page
      });
    }
  };
  const handleNextClick = () => {
    if (store.account.mfaFlowStore.isEnabled) {
      track(events.clicked('Next Step'), {
        page
      });
    } else {
      track(events.clicked('Skip MFA'), {
        page
      });
    }
    navigate('/company_details');
  };
  const countdownFinished = () => {
    store.account.mfaFlowStore.timerIsFinished();
  };
  const sendCodeFormik = useFormik({
    initialValues: {
      phone: isValidE164(userData === null || userData === void 0 ? void 0 : userData.phone_number) ? userData.phone_number : ''
    },
    validationSchema: mfaPhoneSchema,
    onSubmit: async values => {
      const data = {
        phone: values.phone
      };
      const resp = await store.account.updatePhoneNumberAsync.run(data);
      if (resp && resp.isSuccessful) {
        store.account.mfaFlowStore.codeIsSend();
      }
    }
  });
  const checkCodeFormik = useFormik({
    initialValues: mfaSmsCodeInitialValues,
    validationSchema: mfaSmsCodeSchema,
    onSubmit: async values => {
      const data = {
        code: values.code.replace(/[^0-9+]/g, '')
      };
      const resp = await store.account.confirmPhoneNumberAsync.run(data);
      if (resp && resp.isSuccessful) {
        await store.account.enableAccountMfaAsync.run();
        await store.account.getProfileAsync.run();
        store.account.mfaFlowStore.activateMfa();
      }
    }
  });
  const leftBlockContent = /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    className: classes.lgSection
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "h2",
    color: "white"
  }, t('sign_up_flow.mfa_header'))), /*#__PURE__*/React.createElement("div", {
    className: classes.lgSection
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "body1",
    color: "white"
  }, t('sign_up_flow.mfa_desc'))));
  const breadcrumbs = [{
    title: t('sign_up_flow.mfa'),
    icon: /*#__PURE__*/React.createElement(Icon, {
      type: "edit",
      fontSize: "small"
    })
  }, {
    title: t('sign_up_flow.business_information'),
    isDisabled: true
  }, {
    title: t('sign_up_flow.payment_information'),
    isDisabled: true
  }];
  useEffect(() => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return /*#__PURE__*/React.createElement(SplitPageContainer, {
    imageScr: inviteSvg,
    leftBlock: leftBlockContent,
    breadcrumbs: breadcrumbs,
    showLogOutButton: true
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "h3",
    gutterBottom: true
  }, t('mfa.two_factor_auth_optional')), /*#__PURE__*/React.createElement(Typography, {
    variant: "body3",
    component: "p",
    marginBottom: "18px"
  }, t('mfa.two_factor_auth_hint'), " "), !store.account.mfaFlowStore.mfaIsActivated && /*#__PURE__*/React.createElement(Button, {
    variant: "contained",
    onClick: toggleAuthEnabling,
    fullWidth: true,
    'custom-attribute': '850-twoFactor-auth-page'
  }, store.account.mfaFlowStore.isEnabled ? t('general.disable') : t('general.enable'), ' ', t('mfa.two_factor_auth_optional')), store.account.mfaFlowStore.isEnabled && /*#__PURE__*/React.createElement("div", null, !store.account.mfaFlowStore.mfaIsActivated && /*#__PURE__*/React.createElement("div", {
    className: "".concat(classes.authFieldWrapper, " ").concat(store.account.mfaFlowStore.codeIsSent ? classes.success : '')
  }, /*#__PURE__*/React.createElement("form", {
    onSubmit: sendCodeFormik.handleSubmit
  }, /*#__PURE__*/React.createElement(Grid, {
    container: true,
    columnSpacing: 10,
    rowSpacing: 18
  }, /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 12
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "body1"
  }, t('mfa.enter_phone_number_auth'))), /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 12,
    md: 8
  }, /*#__PURE__*/React.createElement(PhoneNumberField, {
    id: "phone",
    name: "phone",
    label: t('general.phone_number'),
    countryLabel: t('mfa.country'),
    value: sendCodeFormik.values.phone,
    onChange: e164 => sendCodeFormik.setFieldValue('phone', e164),
    onBlur: () => {
      sendCodeFormik.setFieldTouched('phone', true);
      track(events.filled('Phone'), {
        page
      });
    },
    disabled: store.account.updatePhoneNumberAsync.inProgress || store.account.mfaFlowStore.timeCounted || store.account.mfaFlowStore.mfaIsActivated,
    error: sendCodeFormik.touched.phone && Boolean(sendCodeFormik.errors.phone),
    helperText: sendCodeFormik.touched.phone && sendCodeFormik.errors.phone ? t('mfa.invalid_phone') : t('mfa.sms_intl_notice')
  })), /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 12,
    md: 4
  }, /*#__PURE__*/React.createElement(Button, {
    type: "submit",
    variant: "text",
    disabled: !sendCodeFormik.isValid || !sendCodeFormik.values.phone || store.account.updatePhoneNumberAsync.inProgress || store.account.mfaFlowStore.timeCounted || store.account.mfaFlowStore.mfaIsActivated,
    fullWidth: !store.account.mfaFlowStore.codeIsSent,
    onClick: () => track(events.clicked('Send Code'), {
      page
    }),
    'custom-attribute': '851-twoFactor-auth-page'
  }, !store.account.mfaFlowStore.codeIsSent ? t('general.send_confirmation_code') : t('general.resend_code'))), store.account.mfaFlowStore.codeIsSent && !store.account.mfaFlowStore.mfaIsActivated && store.account.mfaFlowStore.timeCounted && /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 12
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "body2"
  }, t('mfa.we_will_resend_code'), /*#__PURE__*/React.createElement(Countdown, {
    date: Date.now() + 59000,
    onComplete: countdownFinished,
    renderer: _ref => {
      let seconds = _ref.seconds;
      return /*#__PURE__*/React.createElement("b", null, " ", seconds, " ");
    }
  }), t('general.seconds')))))), store.account.mfaFlowStore.codeIsSent && !store.account.mfaFlowStore.mfaIsActivated && /*#__PURE__*/React.createElement("div", {
    className: "".concat(classes.authFieldWrapper, " ").concat(store.account.mfaFlowStore.mfaActivateFailed ? classes.error : store.account.mfaFlowStore.mfaIsActivated ? classes.success : '')
  }, /*#__PURE__*/React.createElement("form", {
    onSubmit: checkCodeFormik.handleSubmit
  }, /*#__PURE__*/React.createElement(Grid, {
    container: true,
    columnSpacing: 10,
    rowSpacing: 18
  }, /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 12
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "body1"
  }, t('mfa.code_was_sent'))), /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 5
  }, /*#__PURE__*/React.createElement(InputWithIcon, {
    icon: /*#__PURE__*/React.createElement(Icon, {
      type: "shield-keyhole"
    })
  }, /*#__PURE__*/React.createElement(InputMask, {
    onBlur: e => {
      if (e.target.value) checkCodeFormik.handleBlur(e);
      track(events.filled('Auth Code'), {
        page
      });
    },
    onChange: checkCodeFormik.handleChange,
    disabled: store.account.confirmPhoneNumberAsync.inProgress,
    mask: "99-99-99",
    value: checkCodeFormik.values.code
  }, inputProps => /*#__PURE__*/React.createElement(TextField, _extends({}, inputProps, {
    id: "code",
    label: t('mfa.enter_code_auth'),
    name: "code",
    type: "tel",
    variant: "outlined",
    disabled: store.account.confirmPhoneNumberAsync.inProgress,
    fullWidth: true,
    helperText: checkCodeFormik.touched.code && t(checkCodeFormik.errors.code),
    error: checkCodeFormik.touched.code && Boolean(checkCodeFormik.errors.code)
  }))))), /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 7
  }, /*#__PURE__*/React.createElement(Button, {
    type: "submit",
    variant: "text",
    disabled: !checkCodeFormik.isValid || !Object.keys(checkCodeFormik.touched).length || store.account.mfaFlowStore.mfaIsActivated,
    onClick: () => track(events.clicked('Verify Code'), {
      page
    }),
    'custom-attribute': '852-twoFactor-auth-page'
  }, t('buttons.verify')))))), store.account.mfaFlowStore.mfaIsActivated && /*#__PURE__*/React.createElement("div", {
    className: "".concat(classes.authFieldWrapper, " ").concat(classes.success)
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.authIcon
  }, /*#__PURE__*/React.createElement(Icon, {
    color: "success",
    fontSize: "large",
    type: "shield-check"
  })), /*#__PURE__*/React.createElement(Typography, {
    variant: "h5"
  }, t('mfa.two_factor_auth_enabled')))), /*#__PURE__*/React.createElement("div", {
    className: classes.smSection
  }, /*#__PURE__*/React.createElement(Button, {
    id: "skipMfa",
    disabled: store.account.mfaFlowStore.isEnabled && !store.account.mfaFlowStore.mfaIsActivated,
    variant: "text",
    onClick: handleNextClick,
    fullWidth: true,
    'custom-attribute': '853-twoFactor-auth-page'
  }, store.account.mfaFlowStore.isEnabled ? t('buttons.next') : t('buttons.skip'))));
});